import React, { useEffect, useMemo } from "react";
import { Grid, TextInput, Text, Textarea } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import {
  getArFormValues,
  getPrimaryConsultantFromForm,
  resolveAdvisorDeclaration,
  setArFormNestedField,
  syncAdvisorDeclaration,
} from "../helpers/arFormUtils";
import { isWidgetReadOnly } from "../helpers/formReadOnly";
import { DATE_INPUT_FORMAT } from "../helpers/dateInputFormat";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";

const declarationFieldStyles = {
  label: {
    minHeight: 44,
    lineHeight: 1.35,
    display: "block",
    whiteSpace: "normal",
  },
};

const BilingualFieldLabel = ({ english, chinese }) => (
  <>
    {english}
    <br />
    {chinese}
  </>
);

const DisplayField = ({ label, value }) => (
  <div>
    <Text size="xs" color="dimmed" mb={4}>
      {label}
    </Text>
    <Text size="sm">{value || "—"}</Text>
  </div>
);

const toDateOrNull = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const OrderArFormPartD = ({ form, readOnly = false, disabled = false }) => {
  const arForm = getArFormValues(form);
  const consultant = useMemo(
    () => getPrimaryConsultantFromForm(form),
    [form.values?.orderId?.consultants]
  );
  const locked = isWidgetReadOnly({ disabled, readOnly, form });

  useEffect(() => {
    syncAdvisorDeclaration(form, consultant);
  }, [form, consultant]);

  const advisorDeclaration = resolveAdvisorDeclaration(
    arForm.advisorDeclaration,
    consultant
  );
  const signDate = toDateOrNull(advisorDeclaration.signDate);

  return (
    <OrderArFormStack>
      <OrderArFormBlock
        title="Advisor declaration"
        subtitle="理財顧問聲明"
      >
        <Grid gutter="md" align="flex-start">
          <Grid.Col span={12} md={6}>
            <TextInput
              styles={declarationFieldStyles}
              label={
                <BilingualFieldLabel
                  english="Signature of Advisor"
                  chinese="理財顧問簽署"
                />
              }
              value={advisorDeclaration.signature ?? ""}
              readOnly={locked}
              disabled={locked}
              onChange={(e) =>
                setArFormNestedField(
                  form,
                  "advisorDeclaration.signature",
                  e.currentTarget.value
                )
              }
            />
          </Grid.Col>
          <Grid.Col span={12} md={6}>
            <DatePicker
              styles={declarationFieldStyles}
              label={
                <BilingualFieldLabel
                  english={`Sign Date (${DATE_INPUT_FORMAT})`}
                  chinese="簽署日期"
                />
              }
              value={signDate}
              onChange={(date) =>
                setArFormNestedField(
                  form,
                  "advisorDeclaration.signDate",
                  date
                )
              }
              inputFormat={DATE_INPUT_FORMAT}
              placeholder={DATE_INPUT_FORMAT}
              clearable={!locked}
              allowFreeInput={!locked}
              readOnly={locked}
              disabled={locked}
            />
          </Grid.Col>
          <Grid.Col span={12} md={6}>
            <DisplayField
              label={
                <BilingualFieldLabel
                  english="Staff Code"
                  chinese="員工編號"
                />
              }
              value={advisorDeclaration.staffCode}
            />
          </Grid.Col>
          <Grid.Col span={12} md={6}>
            <DisplayField
              label={
                <BilingualFieldLabel
                  english="SFC CE No."
                  chinese="證監會中央編號"
                />
              }
              value={advisorDeclaration.sfcCeNo}
            />
          </Grid.Col>
        </Grid>
      </OrderArFormBlock>

      <OrderArFormBlock title="Remark" subtitle="備註">
        <Textarea
          value={advisorDeclaration.remark ?? ""}
          onChange={(e) =>
            setArFormNestedField(
              form,
              "advisorDeclaration.remark",
              e.currentTarget.value
            )
          }
          minRows={3}
          autosize
        />
      </OrderArFormBlock>
    </OrderArFormStack>
  );
};

export default OrderArFormPartD;
